import { useId } from "react";

import { cn } from "./cn";
import "./spark.css";

export interface SparkChartProps {
  values: number[];
  /** A resolved CSS color, e.g. one entry from useCategoricalColors(...) (see
   *  chart-color.ts) or a status token like var(--rc-color-danger). The
   *  component only knows about colors, never entities. */
  color: string;
  className?: string;
}

/**
 * Mini area sparkline: a stat tile's own bucket-by-bucket shape, gradient fill
 * + line + a dot at the latest point. Purely decorative (aria-hidden) — the
 * tile's own value + label already carry the number this draws.
 */
export function SparkChart({ values, color, className }: SparkChartProps) {
  const gradId = useId();
  const W = 120;
  const H = 34;
  const max = Math.max(...values, 1);
  const n = Math.max(values.length - 1, 1);
  const pts = values.map((v, i) => [(i / n) * W, H - 3 - (v / max) * (H - 8)] as const);
  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`);
  return (
    <svg
      className={cn("rc-spark", className)}
      viewBox={`0 0 ${W} ${H}`}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={`${line.join(" ")} L${W},${H} L0,${H} Z`} fill={`url(#${gradId})`} />
      <path d={line.join(" ")} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
      <circle
        className="rc-spark__dot"
        cx={pts[pts.length - 1]?.[0] ?? W}
        cy={pts[pts.length - 1]?.[1] ?? H - 3}
        r={2.5}
        fill={color}
      />
    </svg>
  );
}
